import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, FileText, Download, ShoppingBag, ArrowLeft, Package, Users, Printer } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { getProduct, getRelatedProducts, getProductReviews } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ProductReviews, StarRating } from "@/components/ProductReviews";
import { FolelseskortCarousel } from "@/components/FolelseskortCarousel";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { addItem, hasItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setSummary({ count: 0, average: 0 });
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        return Promise.all([
          getRelatedProducts(slug, 3).catch(() => []),
          getProductReviews(slug).catch(() => ({ count: 0, average: 0 })),
        ]);
      })
      .then(([rel, rev]) => {
        setRelated(rel);
        setSummary({ count: rev.count, average: rev.average });
      })
      .catch(() => navigate("/butikk"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading || !product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-[#6B726C]">{t("loading")}</div>
    );
  }

  const title = lang === "en" ? product.title_en : product.title_no;
  const short = lang === "en" ? product.short_en : product.short_no;
  const desc = lang === "en" ? product.description_en : product.description_no;
  const inCart = hasItem(product.id);

  const handleAdd = () => addItem(product);

  return (
    <div className="bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-16">
        <Link
          to="/butikk"
          data-testid="back-to-shop"
          className="inline-flex items-center gap-2 text-sm text-[#6B726C] hover:text-[#2C302E] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {lang === "en" ? "Back to shop" : "Tilbake til butikk"}
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Image */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            {slug === "folelseskort" ? (
              <FolelseskortCarousel title={title} />
            ) : (
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-white border border-[#E5E2DC]">
                <img
                  src={product.image_url}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium">
                {t("product_format")}
              </p>
              {product.egen_utgivelse && (
                <span
                  className="inline-flex items-center gap-1.5 bg-[#2C302E] text-white rounded-full pl-2 pr-3 py-1 text-[10px] font-semibold tracking-wider uppercase"
                  data-testid="product-detail-badge-egen-utgivelse"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D97B66]" />
                  Egen utgivelse
                </span>
              )}
            </div>
            <h1 className="font-heading text-3xl md:text-5xl tracking-tight font-light text-[#2C302E] leading-tight">
              {title}
            </h1>
            {product.egen_utgivelse && product.author_name && (
              <p
                className="mt-2 text-sm text-[#6B726C]"
                data-testid="product-detail-author"
              >
                {lang === "en" ? "Written by" : "Skrevet av"}{" "}
                <span className="text-[#2C302E] font-medium">{product.author_name}</span>
              </p>
            )}
            {summary.count > 0 && (
              <Link
                to="#reviews"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="product-reviews"]')?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="product-rating-summary"
                className="mt-3 inline-flex items-center gap-2 text-sm text-[#6B726C] hover:text-[#2C302E]"
              >
                <StarRating value={summary.average} size={14} />
                <span className="font-medium text-[#2C302E]">{summary.average.toFixed(1)}</span>
                <span>
                  ({summary.count}{" "}
                  {lang === "en"
                    ? summary.count === 1
                      ? "review"
                      : "reviews"
                    : summary.count === 1
                    ? "anmeldelse"
                    : "anmeldelser"}
                  )
                </span>
              </Link>
            )}
            <p className="mt-5 text-lg text-[#6B726C] leading-relaxed">{short}</p>

            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-heading text-4xl md:text-5xl text-[#2C302E] font-medium">
                {product.price_nok}
              </span>
              <span className="text-base text-[#6B726C]">NOK</span>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border border-[#E5E2DC] rounded-xl px-4 py-3 bg-white">
                <FileText className="w-5 h-5 text-[#8BA896] mb-2" strokeWidth={1.5} />
                <p className="text-xs text-[#6B726C] uppercase tracking-widest">
                  {lang === "en" ? "Pages" : "Sider"}
                </p>
                <p className="font-heading text-lg text-[#2C302E] font-medium">
                  {product.pages}
                </p>
              </div>
              <div className="border border-[#E5E2DC] rounded-xl px-4 py-3 bg-white">
                <Download className="w-5 h-5 text-[#8BA896] mb-2" strokeWidth={1.5} />
                <p className="text-xs text-[#6B726C] uppercase tracking-widest">
                  {lang === "en" ? "Delivery" : "Levering"}
                </p>
                <p className="font-heading text-lg text-[#2C302E] font-medium">
                  {lang === "en" ? "Instant" : "Umiddelbar"}
                </p>
              </div>
            </div>

            <button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              disabled={inCart}
              className={`mt-8 w-full rounded-full px-8 py-4 font-medium transition-all flex items-center justify-center gap-3 ${
                inCart
                  ? "bg-[#8BA896] text-white cursor-default"
                  : "bg-[#2C302E] hover:bg-[#1f221f] text-white"
              }`}
            >
              {inCart ? (
                <>
                  <Check className="w-5 h-5" strokeWidth={2} />
                  {t("product_in_cart")}
                </>
              ) : (
                <>
                  <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                  {t("product_add_cart")}
                </>
              )}
            </button>

            {inCart && (
              <Link
                to="/handlekurv"
                data-testid="go-to-cart-link"
                className="mt-3 block text-center text-sm text-[#D97B66] hover:text-[#C66A55]"
              >
                {t("cart_checkout")} →
              </Link>
            )}

            <div className="mt-12 pt-8 border-t border-[#E5E2DC]">
              <h2 className="font-heading text-xl text-[#2C302E] font-medium mb-4">
                {t("product_about")}
              </h2>
              <p className="text-[#6B726C] leading-relaxed whitespace-pre-line">
                {desc}
              </p>
            </div>
          </div>
        </div>

        {/* Detailed description — what's inside / who it's for / how to use */}
        {(() => {
          const details = lang === "en" ? product.details_en : product.details_no;
          if (!details || (!details.contains && !details.suits && !details.how_to_use)) return null;
          const sections = [
            {
              icon: Package,
              eyebrow: lang === "en" ? "What's inside" : "Hva inneholder produktet",
              items: details.contains || [],
              color: "#8BA896",
              softBg: "#EFF5EE",
            },
            {
              icon: Users,
              eyebrow: lang === "en" ? "Who it's for" : "Hvem passer dette for",
              items: details.suits || [],
              color: "#D97B66",
              softBg: "#FCE6E3",
            },
            {
              icon: Printer,
              eyebrow: lang === "en" ? "How to use it" : "Hvordan bruker jeg det",
              items: details.how_to_use || [],
              color: "#5A8A9A",
              softBg: "#E1EDF1",
            },
          ];
          return (
            <section
              data-testid="product-details"
              className="mt-20 pt-16 border-t border-[#E5E2DC]"
            >
              <div className="grid lg:grid-cols-[1fr_360px] gap-12 items-start">
                <div className="space-y-10">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium mb-3">
                      {lang === "en" ? "Product details" : "Detaljer"}
                    </p>
                    <h2 className="font-heading text-3xl md:text-4xl text-[#2C302E] font-light leading-tight">
                      {lang === "en"
                        ? "Everything you need to know"
                        : "Alt du trenger å vite"}
                    </h2>
                  </div>

                  {sections.map((s, i) =>
                    s.items.length === 0 ? null : (
                      <div key={i} className="flex gap-5">
                        <div
                          className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
                          style={{ backgroundColor: s.softBg }}
                        >
                          <s.icon
                            className="w-5 h-5"
                            style={{ color: s.color }}
                            strokeWidth={1.6}
                          />
                        </div>
                        <div className="flex-1 pt-1">
                          <p
                            className="text-xs uppercase tracking-[0.2em] font-semibold mb-3"
                            style={{ color: s.color }}
                          >
                            {s.eyebrow}
                          </p>
                          <ul className="space-y-2.5">
                            {s.items.map((item, j) => (
                              <li
                                key={j}
                                className="flex items-start gap-3 text-[#2C302E] leading-relaxed"
                              >
                                <span
                                  className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2.5"
                                  style={{ backgroundColor: s.color }}
                                ></span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Preview image (sticky on desktop) */}
                {product.preview_image_url && (
                  <div className="lg:sticky lg:top-24">
                    <p className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium mb-3">
                      {lang === "en" ? "Sample page" : "Eksempelside"}
                    </p>
                    <div
                      data-testid="product-preview-image"
                      className="bg-white border border-[#E5E2DC] rounded-2xl p-3 shadow-[0_12px_40px_-15px_rgba(44,48,46,0.15)]"
                    >
                      <img
                        src={product.preview_image_url}
                        alt={`${title} – ${lang === "en" ? "preview" : "forhåndsvisning"}`}
                        className="w-full rounded-xl border border-[#F0EDE7]"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-xs text-[#6B726C] mt-3 text-center italic">
                      {lang === "en"
                        ? "A glimpse of what's inside the PDF"
                        : "Et lite glimt av hva som er i PDF-en"}
                    </p>
                  </div>
                )}
              </div>
            </section>
          );
        })()}

        {/* Reviews */}
        <ProductReviews slug={slug} />

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-24 pt-16 border-t border-[#E5E2DC]">
            <h3 className="font-heading text-2xl md:text-3xl text-[#2C302E] font-light mb-10">
              {t("product_related")}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} idx={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
